import Vue from 'vue';
import SurveyResponsesAPI from '../../api/surveyResponses';
import { throwErrorMessage } from '../utils/api';
import {
  computeNpsScore,
  csatCounts,
  csatSatisfactionScore,
  responseRate,
  segmentCounts,
} from '../../helper/surveyMetrics';

export const state = {
  records: [],
  meta: { currentPage: 1, totalCount: 0 },
  metrics: {
    totalCount: 0,
    totalSentCount: 0,
    ratingsCount: {},
    timeseries: {},
    grouped: {},
  },
  // Comentários chegam sob demanda, por resposta — carregar todos junto da lista seria
  // uma consulta grande para algo que quase ninguém abre.
  notesByResponseId: {},
  uiFlags: {
    isFetching: false,
    isFetchingMetrics: false,
    isDownloading: false,
    isFetchingNotes: false,
    isCommenting: false,
  },
};

export const getters = {
  getResponses: $state => $state.records,
  getMeta: $state => $state.meta,
  getMetrics: $state => $state.metrics,
  getNotes: $state => responseId => $state.notesByResponseId[responseId] || [],
  getUIFlags: $state => $state.uiFlags,

  // As derivações ficam no helper puro; o getter só junta com o estado.
  getSegmentCounts: $state => segmentCounts($state.metrics.ratingsCount),
  getNpsScore: $state => {
    const counts = segmentCounts($state.metrics.ratingsCount);
    return computeNpsScore({
      promoters: counts.promoters,
      detractors: counts.detractors,
      total: counts.total,
    });
  },
  getSatisfactionScore: $state =>
    csatSatisfactionScore(csatCounts($state.metrics.ratingsCount)),
  getResponseRate: $state =>
    responseRate({
      responses: $state.metrics.totalCount,
      sent: $state.metrics.totalSentCount,
    }),
};

// #1552: trocar de preset rapidamente (ex.: "último ano" → "7 dias") disparava duas
// chamadas concorrentes, e a resposta lenta da primeira podia chegar DEPOIS da segunda e
// sobrescrever dados mais novos com dados de um período que já não é o exibido. Os
// contadores abaixo vivem no escopo do módulo (não no state, que é observado pelo Vuex) e
// servem só para descartar respostas que não são mais a última chamada em voo quando
// resolvem — sem AbortController, sem mudar a camada HTTP.
let latestListRequest = 0;
let latestMetricsRequest = 0;

export const actions = {
  get: async ({ commit }, params = {}) => {
    latestListRequest += 1;
    const requestId = latestListRequest;
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await SurveyResponsesAPI.get(params);
      if (requestId !== latestListRequest) return [];

      commit('SET_RESPONSES', data.payload || []);
      commit('SET_META', {
        currentPage: data.meta?.current_page || 1,
        totalCount: data.meta?.total_count || 0,
      });
      return data.payload || [];
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      // No `finally` porque `throwErrorMessage` sempre lança: fora dele, uma falha deixaria
      // a tela carregando para sempre.
      if (requestId === latestListRequest) {
        commit('SET_UI_FLAG', { isFetching: false });
      }
    }
  },

  getMetrics: async ({ commit }, params = {}) => {
    latestMetricsRequest += 1;
    const requestId = latestMetricsRequest;
    commit('SET_UI_FLAG', { isFetchingMetrics: true });
    try {
      const { data } = await SurveyResponsesAPI.getMetrics(params);
      if (requestId !== latestMetricsRequest) return null;

      commit('SET_METRICS', {
        totalCount: data.total_count || 0,
        totalSentCount: data.total_sent_count || 0,
        ratingsCount: data.ratings_count || {},
        timeseries: data.timeseries || {},
        grouped: data.grouped || {},
      });
      return data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      if (requestId === latestMetricsRequest) {
        commit('SET_UI_FLAG', { isFetchingMetrics: false });
      }
    }
  },

  download: async ({ commit }, params = {}) => {
    commit('SET_UI_FLAG', { isDownloading: true });
    try {
      const { data } = await SurveyResponsesAPI.download(params);
      return data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isDownloading: false });
    }
  },

  fetchNotes: async ({ commit }, responseId) => {
    commit('SET_UI_FLAG', { isFetchingNotes: true });
    try {
      const { data } = await SurveyResponsesAPI.getNotes(responseId);
      commit('SET_NOTES', { responseId, notes: data.payload || [] });
      return data.payload || [];
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      commit('SET_UI_FLAG', { isFetchingNotes: false });
    }
  },

  createNote: async ({ commit, dispatch }, { responseId, content }) => {
    commit('SET_UI_FLAG', { isCommenting: true });
    try {
      await SurveyResponsesAPI.createNote(responseId, content);
      await dispatch('fetchNotes', responseId);
      commit('INCREMENT_NOTES_COUNT', responseId);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { isCommenting: false });
    }
  },

  deleteNote: async ({ commit, dispatch }, { responseId, noteId }) => {
    commit('SET_UI_FLAG', { isCommenting: true });
    try {
      await SurveyResponsesAPI.deleteNote(responseId, noteId);
      await dispatch('fetchNotes', responseId);
      commit('DECREMENT_NOTES_COUNT', responseId);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { isCommenting: false });
    }
  },
};

const findResponse = ($state, responseId) =>
  $state.records.find(record => record.id === Number(responseId));

export const mutations = {
  SET_UI_FLAG($state, uiFlags) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlags };
  },
  SET_RESPONSES($state, records) {
    Vue.set($state, 'records', records);
  },
  SET_META($state, meta) {
    Vue.set($state, 'meta', meta);
  },
  SET_METRICS($state, metrics) {
    Vue.set($state, 'metrics', metrics);
  },
  SET_NOTES($state, { responseId, notes }) {
    Vue.set($state.notesByResponseId, responseId, notes);
  },
  // Mantém o contador da linha em dia sem recarregar a lista inteira.
  INCREMENT_NOTES_COUNT($state, responseId) {
    const record = findResponse($state, responseId);
    if (record) Vue.set(record, 'notes_count', (record.notes_count || 0) + 1);
  },
  DECREMENT_NOTES_COUNT($state, responseId) {
    const record = findResponse($state, responseId);
    if (record) {
      Vue.set(
        record,
        'notes_count',
        Math.max((record.notes_count || 1) - 1, 0)
      );
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
